import { PENALTY_TYPE_LABEL, type PenaltyType, rm } from '../penalties/penalties.js';

/**
 * 381 (#1271): why an account was banned, in one place for the dialog, the user page, the Users list and the history,
 * so the four say it the same way. The platform decides and stores; this module only words it.
 */
export type BanReason = 'unpaid_penalty' | 'accident' | 'other';

export const BAN_REASON_LABEL: Record<BanReason, string> = {
  unpaid_penalty: "Didn't pay a penalty",
  accident: 'Accident',
  other: 'Other',
};

export const BAN_NOTE_MAX = 300;

/** `User.ban` as staff read it (`GET /users/:id`, `GET /users`). */
export interface BanInfo {
  reason: BanReason;
  penalty_id: string | null;
  booking_ref: string;
  penalty_type: string;
  amount_minor: number | null;
  note: string;
  by_name: string;
  at: string;
}

/** One of the customer's unpaid penalties, offered by the ban dialog. */
export interface UnpaidPenalty {
  id: string;
  booking_ref: string;
  type: string;
  amount_minor: number;
  on: string;
}

const penaltyTypeLabel = (t: string) => PENALTY_TYPE_LABEL[t as PenaltyType] ?? t;

/** "Booking #4dd13c0a · Late return · RM 50.00", from a penalty or a stored ban. */
export function penaltyLine(p: {
  booking_ref: string;
  type?: string;
  penalty_type?: string;
  amount_minor: number | null;
}) {
  const type = p.type ?? p.penalty_type ?? '';
  return [
    `Booking #${p.booking_ref}`,
    type ? penaltyTypeLabel(type) : null,
    p.amount_minor != null ? rm(p.amount_minor) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

const isReason = (v: unknown): v is BanReason =>
  v === 'unpaid_penalty' || v === 'accident' || v === 'other';

/** The reason's short form for a list cell: "Didn't pay a penalty · #4dd13c0a", "Accident". */
export function banShort(
  ban: Pick<BanInfo, 'reason' | 'booking_ref'> | null | undefined,
): string | null {
  if (!ban || !isReason(ban.reason)) return null;
  return ban.reason === 'unpaid_penalty' && ban.booking_ref
    ? `${BAN_REASON_LABEL.unpaid_penalty} · #${ban.booking_ref}`
    : BAN_REASON_LABEL[ban.reason];
}

/**
 * A `status_changed` entry in the history: "Banned: Accident" with the penalty or the note; "Account enabled" when it
 * leaves banned; otherwise "Status changed: active → inactive". Older bans (before 381) carry no reason.
 */
export function statusEventSummary(m: Record<string, unknown>): {
  line: string;
  detail?: string;
  note?: string;
} {
  const to = typeof m.to_status === 'string' ? m.to_status : '';
  const from = typeof m.from_status === 'string' ? m.from_status : '';
  if (to === 'banned') {
    if (!isReason(m.ban_reason)) return { line: 'Banned' };
    const detail =
      m.ban_reason === 'unpaid_penalty' && typeof m.booking_ref === 'string'
        ? penaltyLine({
            booking_ref: m.booking_ref,
            penalty_type: typeof m.penalty_type === 'string' ? m.penalty_type : '',
            amount_minor: typeof m.amount_minor === 'number' ? m.amount_minor : null,
          })
        : undefined;
    return {
      line: `Banned: ${BAN_REASON_LABEL[m.ban_reason]}`,
      detail,
      note: typeof m.note === 'string' && m.note ? m.note : undefined,
    };
  }
  if (from === 'banned' && to === 'active') return { line: 'Account enabled' };
  return { line: `Status changed: ${from || '—'} → ${to || '—'}` };
}
